import { Card } from "primereact/card";
import "../specialty-assistant/PrintableReportStyles.css";
import styles from "./Sl2Verification.module.css";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import {
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { sl2Verification } from "../../api/routes";
import { Sl2VerificationResponse } from "../../client";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import { Button } from "primereact/button";
import { sleepMs } from "../../util";
import classNames from "classnames";
import { Wordmark } from "../../components/wordmark/Wordmark";
import { Editor } from "@tinymce/tinymce-react";
import { Toast } from "primereact/toast";
import { ThemeContext } from "../../themes/Theme";
import { IfFeatureEnabled } from "../../IfFeatureEnabled";

const ResultContainer = ({
  verificationResult,
  forPrint,
  button,
}: {
  verificationResult: Sl2VerificationResponse;
  forPrint: boolean;
  button?: ReactNode;
}) => {
  return (
    <div
      className={classNames(
        styles.resultContainer,
        forPrint && styles.printContainer
      )}
    >
      {forPrint && <Wordmark height="32px" themeOverride="light" />}
      <div className={styles.horizontalContainer}>
        <span style={{ fontWeight: "bold" }}>
          {verificationResult.filename}
        </span>
        {button}
      </div>
      {verificationResult.document_warnings.length === 0 &&
        verificationResult.document_errors.length === 0 && (
          <div>
            <Message severity="success" text="No issues found" />
          </div>
        )}
      {verificationResult.document_errors.map((documentError) => (
        <div key={documentError}>
          <Message key={documentError} severity="error" text={documentError} />
        </div>
      ))}
      {verificationResult.document_warnings.map((documentWarning) => (
        <div key={documentWarning}>
          <Message
            key={documentWarning}
            severity="warn"
            text={documentWarning}
          />
        </div>
      ))}
    </div>
  );
};

export const TemplateEditor = ({
  toast,
  template,
}: {
  toast: RefObject<Toast>;
  template: string;
}) => {
  const [text, setText] = useState(template);
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  return (
    <Editor
      value={text}
      init={{
        ...{
          height: 500,
          menubar: false,
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "charmap",
            "table",
            "help",
          ],
          toolbar:
            "bold italic underline | table | numlist bullist | customcopy customdownload clearedits",
          setup: (editor) => {
            editor.ui.registry.addButton("clearedits", {
              icon: "reload",
              tooltip: "Clear all Edits",
              onAction: async () => {
                setText(template);
              },
            });
            editor.ui.registry.addButton("customcopy", {
              icon: "copy",
              tooltip: "Copy content",
              onAction: async () => {
                const content = editor.getContent({ format: "html" });
                const textContent = editor.getContent({ format: "text" });
                const htmlBlob = new Blob([content], { type: "text/html" });
                const plainBlob = new Blob([textContent], {
                  type: "text/plain",
                });

                try {
                  await navigator.clipboard.write([
                    new ClipboardItem({
                      "text/html": htmlBlob,
                      "text/plain": plainBlob,
                    }),
                  ]);
                } catch (err) {
                  // Fallback to text-only if clipboard.write not supported
                  navigator.clipboard.writeText(content);
                }

                toast.current?.show({
                  severity: "success",
                  summary: "Copied",
                  detail: "Message copied to clipboard",
                  life: 5000,
                });
              },
            });
          },
          content_style:
            'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px }',
        },
        ...(isLightMode ? {} : { skin: "oxide-dark", content_css: "dark" }),
      }}
      onEditorChange={(content) => {
        setText(content);
      }}
    />
  );
};

export const Sl2Verification = () => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState<
    Sl2VerificationResponse | undefined
  >(undefined);
  const onUpload = async (event: FileUploadHandlerEvent) => {
    const file = event.files[0];
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please upload a PDF file",
        sticky: true,
      });
      return;
    }
    setVerificationResult(undefined);
    setIsLoading(true);
    const result = await sl2Verification(file);
    fileUploadRef.current?.clear();
    setIsLoading(false);
    setVerificationResult(result);
  };

  const [isPrinting, setIsPrinting] = useState(false);
  useEffect(() => {
    (async () => {
      if (isPrinting) {
        const oldTitle = document.title;
        if (verificationResult) {
          document.title =
            verificationResult.filename.slice(0, -4) + "_pantheon.pdf";
        }
        await sleepMs(0);
        window.print();
        await sleepMs(0);
        document.title = oldTitle;
        setIsPrinting(false);
      }
    })();
  }, [isPrinting]);

  return (
    <>
      <div className={styles.cardContainer}>
        <Toast ref={toast} />
        <Card title="SL-2 Verification" className={styles.card}>
          <FileUpload
            disabled={isLoading}
            ref={fileUploadRef}
            name="pdfFile"
            customUpload
            uploadHandler={onUpload}
            emptyTemplate={
              <span>
                <span className="pi pi-file-pdf"></span> Drag and drop a PDF
                file here to upload.
              </span>
            }
            chooseOptions={{
              icon: "pi pi-file-pdf",
              iconOnly: false,
            }}
            uploadOptions={{
              icon: "pi pi-upload",
              iconOnly: false,
            }}
            cancelOptions={{
              icon: "pi pi-times",
              iconOnly: false,
            }}
            progressBarTemplate={
              isLoading ? (
                <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
              ) : (
                <></>
              )
            }
          />
          {verificationResult && (
            <>
              <ResultContainer
                verificationResult={verificationResult}
                forPrint={false}
                button={
                  <Button
                    className={styles.textButton}
                    onClick={() => setIsPrinting(true)}
                    severity="secondary"
                    text
                    icon="pi pi-download"
                    tooltip="Download report"
                    tooltipOptions={{ position: "bottom" }}
                  />
                }
              />
            </>
          )}
        </Card>
        {(verificationResult?.document_errors?.length ?? 0) > 0 && (
          <IfFeatureEnabled feature="sl2-verification-template-editor">
            <Card title="Response Editor" className={styles.card}>
              <TemplateEditor
                toast={toast}
                template={verificationResult?.template ?? ""}
              />
            </Card>
          </IfFeatureEnabled>
        )}
      </div>
      {verificationResult && (
        <ResultContainer
          verificationResult={verificationResult}
          forPrint={true}
        />
      )}
    </>
  );
};
